import React, { ReactNode } from 'react';
import {
  Box,
  Checkbox,
  Collapse,
  IconButton,
  List,
  ListSubheader,
  Table,
  TableBody,
  TableCell,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  HeadCell,
  RowCell,
  useIndexTableContext,
} from '../../index_table.context';
import {
  HoverableRowContext,
  HoverableRowProvider,
} from './HoverableRow.context';
import { TableRow } from './TableRow';

interface HoverableRowProps {
  row: RowCell;
  // headCells: HeadCell[];
  // onRowClick?: (event: React.MouseEvent<unknown>, row: RowCell) => void;
  // hoverCell?: boolean;
  // checkboxes?: boolean;
  // expandOnCheckbox?: boolean;
  // selected: readonly number[];
  // setSelected: React.Dispatch<React.SetStateAction<readonly number[]>>;
  // selectMultiple?: boolean;
  // itemBorder?: boolean;
}

export const IndexHoverableRow = ({ row }: HoverableRowProps) => {
  const {
    headCells,
    onRowClick,
    hoverCell = false,
    checkboxes,
    expandOnCheckbox,
    selected = [],
    setSelected = () => {},
    selectMultiple = true,
    itemBorder,
    rowBorder,
  } = useIndexTableContext();
  const handleCheckboxClick = (id: number) => {
    let newSelected: readonly number[] = [];
    if (selectMultiple) {
      const selectedIndex = selected.indexOf(id);
      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selected, id);
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selected.slice(1));
      } else if (selectedIndex === selected.length - 1) {
        newSelected = newSelected.concat(selected.slice(0, -1));
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
          selected.slice(0, selectedIndex),
          selected.slice(selectedIndex + 1)
        );
      }
    } else {
      if (!selected.find(s => s === id)) {
        newSelected = newSelected.concat(id);
      }
    }

    setSelected(newSelected);
  };
  const type = typeof row.type === 'function' ? row.type(row) : row.type;
  const isSelected = (id: number) => selected.indexOf(id) !== -1;
  const isItemSelected = isSelected(row.id);
  const RowWrapper = ({ children }: { children: ReactNode }) =>
    row.wrapper ? <row.wrapper>{children}</row.wrapper> : <>{children}</>;
  return (
    <HoverableRowProvider row={row} headCells={headCells}>
      <HoverableRowContext.Consumer>
        {({
          row,
          parentRowHeadCells,
          childRowHeadCell,
          setParentHover,
          setChildHover,
          expanded,
        }) =>
          !row ? null : (
            <RowWrapper>
              <StyledTableRow
                rowBorder={row.rowBorder}
                borderColor={row.itemBorder?.border}
                backgroundColor={row.itemBorder?.background}
                className="parentRow"
                onClick={(event: any) => onRowClick && onRowClick(event, row)}
                tabIndex={-1}
                key={`${row.id}-${row.type}`}
                onMouseEnter={() => {
                  if (!hoverCell) setParentHover(true);
                }}
                onMouseLeave={() => {
                  if (!hoverCell) setParentHover(false);
                }}
                type={type}
                expanded={
                  expandOnCheckbox
                    ? isItemSelected
                    : expanded
                    ? 'true'
                    : 'false'
                }
                disabled={row.disabled && (!isItemSelected || !checkboxes)}
                sx={{ cursor: 'pointer' }}
              >
                {(checkboxes || expandOnCheckbox) && (
                  <TableCell padding="checkbox">
                    {row.id > 0 && (
                      <IconButton
                        onClick={event => {
                          event.preventDefault();
                          event.stopPropagation();
                          handleCheckboxClick(row.id);
                        }}
                      >
                        <Checkbox
                          color="primary"
                          checked={isItemSelected}
                          disabled={row?.disabled}
                        />
                      </IconButton>
                    )}
                  </TableCell>
                )}
                {/* {row.icon && (
                  <TableCell padding="normal">
                    {row.icon}
                  </TableCell>
                )} */}
                {headCells.map((headCell, hIndex) => {
                  return headCell.component ? (
                    <TableCell
                      key={hIndex}
                      align={headCell.align || 'left'}
                      padding={!!headCell.icon ? 'normal' : undefined}
                      onMouseEnter={() => {
                        if (headCell.hover) setChildHover(true);
                      }}
                      onMouseLeave={() => {
                        setChildHover(false);
                      }}
                      onTouchEnd={e => {
                        if (headCell.hover) {
                          e.preventDefault();
                          setChildHover(!expanded);
                        }
                      }}
                      sx={headCell.sx}
                      rowSpan={row[headCell.name]?.rowSpan}
                      colSpan={row[headCell.name]?.colSpan}
                    >
                      <headCell.component
                        key={hIndex}
                        disabled={
                          row?.disabled && (!isItemSelected || !checkboxes)
                        }
                        {...headCell}
                        {...{ [headCell.name]: row[headCell.name] }}
                        {...(headCell.render
                          ? headCell.render(row, headCell.name)
                          : row[headCell.name])}
                      />
                    </TableCell>
                  ) : (
                    <TableCell
                      key={hIndex}
                      align={headCell.align || 'left'}
                      onMouseEnter={() => {
                        if (headCell.hover) setChildHover(true);
                      }}
                      onMouseLeave={() => {
                        setChildHover(false);
                      }}
                      onTouchEnd={e => {
                        if (headCell.hover) {
                          e.preventDefault();
                          setChildHover(!expanded);
                        }
                      }}
                      rowSpan={row[headCell.name]?.rowSpan}
                      colSpan={row[headCell.name]?.colSpan}
                    >
                      {headCell.render
                        ? headCell.render(
                            {
                              ...row,
                              disabled:
                                row?.disabled &&
                                (!isItemSelected || !checkboxes),
                            },
                            headCell.name
                          )
                        : row[headCell.name]}
                    </TableCell>
                  );
                })}
              </StyledTableRow>
              {row.child && (
                <TableRow
                  // onMouseEnter={() => {
                  //   setChildHover(true);
                  // }}
                  // onMouseLeave={() => {
                  //   setChildHover(false);
                  // }}
                  sx={{
                    '& > *': { borderBottom: 'unset !important' },
                  }}
                  type={type}
                  child="true"
                >
                  <TableCell
                    colSpan={
                      row.icon
                        ? Object.keys(headCells).length + 1
                        : Object.keys(headCells).length
                    }
                    sx={{ padding: 0 }}
                  >
                    <Collapse
                      in={expandOnCheckbox ? isItemSelected : expanded}
                      timeout="auto"
                      unmountOnExit
                    >
                      <List
                        dense
                        onMouseEnter={() => {
                          setChildHover(true);
                        }}
                        onMouseLeave={() => {
                          setChildHover(false);
                        }}
                        subheader={row.child?.subheader}
                      >
                        {row.child?.content}
                      </List>
                    </Collapse>
                  </TableCell>
                </TableRow>
              )}
            </RowWrapper>
          )
        }
      </HoverableRowContext.Consumer>
    </HoverableRowProvider>
  );
};

const StyledTableRow = styled(({ disabled, ...props }: any) => (
  <TableRow {...props} />
))(({ theme, disabled, rowBorder, borderColor, backgroundColor }) =>
  rowBorder
    ? rowBorder(theme)
    : borderColor
    ? {
        opacity: disabled ? 0.5 : 1,
        boxShadow: theme.shadows[3],

        borderRadius: '4px',
        backgroundColor: backgroundColor,
        marginTop: '2px',
        marginBottom: '2px',
        width: '100%',
        '& td': {
          borderTop: `1px solid ${borderColor}`, // Customize the border style
          borderBottom: `1px solid ${borderColor}`, // Customize the border style
          backgroundColor: backgroundColor,
        },
        '& td:first-child': {
          borderLeft: `20px solid ${borderColor}`, // Customize the border style
          borderTopLeftRadius: '4px', // Customize the border radius value
          borderBottomLeftRadius: '4px', // Customize the border radius value
        },
        '& td:last-child': {
          borderRight: `1px solid ${borderColor}`, // Customize the border style
          borderTopRightRadius: '4px', // Customize the border radius value
          borderBottomRightRadius: '4px', // Customize the border radius value
        },
      }
    : {
        '&:nth-child(4n+1)': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
          border: '1px solid rgba(0, 0, 0, 0.12)',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            border: '1px solid rgba(0, 0, 0, 0.12)',
          },
        },
        opacity: disabled ? 0.5 : 1,
        boxShadow: theme.shadows[3],
      }
);
